/**
 * The dashboard read path (Phase 1, step 1.4).
 *
 * One call to `app.money_flow` returns every figure the screen shows. The aggregation is in
 * SQL on purpose (DBD-014, decision recorded as DBD-022): the client formats money and never
 * adds it up, and the MCP server will read the same function rather than a second formula.
 */
import type { MoneyFlow, PlanningNature, StabilityClass } from '@pfo/analytics';

import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

export type MoneyFlowQuery = Readonly<{
  /** One person, or the household-subject — whose figures to read. */
  subjectId: string;
  year: number;
  month: number;
}>;

/**
 * The family report (step 1.8, spec 22.3): the same figures for every subject at once.
 *
 * Deliberately a second query rather than a flag on the one above: the two answer different
 * questions and the household one is opt-in, so a screen has to ask for it on purpose.
 */
export type HouseholdMoneyFlowQuery = Readonly<{
  householdId: string;
  year: number;
  month: number;
}>;

export async function fetchMoneyFlow(input: MoneyFlowQuery): Promise<MoneyFlow> {
  const { data, error } = await supabase.schema('app').rpc('money_flow', {
    p_subject_id: input.subjectId,
    p_year: input.year,
    p_month: input.month,
  });
  if (error) throw appErrorException(error);
  if (data === null || typeof data !== 'object') {
    throw appErrorException(new Error('Không đọc được số liệu tháng này.'));
  }

  return toMoneyFlow(data as Record<string, unknown>);
}

export async function fetchHouseholdMoneyFlow(input: HouseholdMoneyFlowQuery): Promise<MoneyFlow> {
  const { data, error } = await supabase.schema('app').rpc('household_money_flow', {
    p_household_id: input.householdId,
    p_year: input.year,
    p_month: input.month,
  });
  if (error) throw appErrorException(error);
  if (data === null || typeof data !== 'object') {
    throw appErrorException(new Error('Không đọc được số liệu tháng này.'));
  }

  return toMoneyFlow(data as Record<string, unknown>);
}

/** jsonb arrives as `unknown`; this is the single place it becomes a typed contract. */
function toMoneyFlow(raw: Record<string, unknown>): MoneyFlow {
  const period = objectAt(raw, 'period');
  const comparison = objectAt(raw, 'comparison');
  const moneyIn = objectAt(raw, 'money_in');
  const allocation = objectAt(raw, 'allocation');
  const expense = objectAt(raw, 'expense');

  return {
    period: {
      year: numberAt(period, 'year'),
      month: numberAt(period, 'month'),
      fromUtc: stringAt(period, 'from_utc'),
      toUtc: stringAt(period, 'to_utc'),
      timezone: stringAt(period, 'timezone'),
      elapsedDays: numberAt(period, 'elapsed_days'),
    },
    comparison: {
      fromUtc: stringAt(comparison, 'from_utc'),
      toUtc: stringAt(comparison, 'to_utc'),
      expenseTotal: numberAt(comparison, 'expense_total'),
      incomeTotal: numberAt(comparison, 'income_total'),
      expenseDelta: numberAt(comparison, 'expense_delta'),
      incomeDelta: numberAt(comparison, 'income_delta'),
    },
    moneyIn: {
      total: numberAt(moneyIn, 'total'),
      stableTotal: numberAt(moneyIn, 'stable_total'),
      variableTotal: numberAt(moneyIn, 'variable_total'),
      receivedFromFamily: numberAt(moneyIn, 'received_from_family'),
      bySource: arrayAt(moneyIn, 'by_source').map((row) => ({
        incomeSourceId: stringAt(row, 'income_source_id'),
        name: stringAt(row, 'name'),
        stabilityClass: stringAt(row, 'stability_class') as StabilityClass,
        amount: numberAt(row, 'amount'),
        sharePct: numberAt(row, 'share_pct'),
      })),
    },
    allocation: {
      expense: numberAt(allocation, 'expense'),
      saving: numberAt(allocation, 'saving'),
      reserve: numberAt(allocation, 'reserve'),
      investment: numberAt(allocation, 'investment'),
      debtPrincipal: numberAt(allocation, 'debt_principal'),
      paidForFamily: numberAt(allocation, 'paid_for_family'),
      receivedFromFamily: numberAt(allocation, 'received_from_family'),
      freeCash: numberAt(allocation, 'free_cash'),
      freeCashStable: numberAt(allocation, 'free_cash_stable'),
    },
    expense: {
      total: numberAt(expense, 'total'),
      byCategory: arrayAt(expense, 'by_category').map(toExpenseSlice),
      byParent: arrayAt(expense, 'by_parent').map(toExpenseSlice),
      byPlanningNature: arrayAt(expense, 'by_planning_nature').map(toPlanningNatureSlice),
    },
  };
}

function toExpenseSlice(row: Record<string, unknown>) {
  return {
    categoryId: nullableStringAt(row, 'category_id'),
    name: nullableStringAt(row, 'name'),
    icon: nullableStringAt(row, 'icon'),
    parentId: nullableStringAt(row, 'parent_id'),
    amount: numberAt(row, 'amount'),
    sharePct: numberAt(row, 'share_pct'),
  };
}

function toPlanningNatureSlice(row: Record<string, unknown>) {
  return {
    planningNature: nullableStringAt(row, 'planning_nature') as PlanningNature | null,
    amount: numberAt(row, 'amount'),
    sharePct: numberAt(row, 'share_pct'),
  };
}

function objectAt(raw: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = raw[key];
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function arrayAt(raw: Record<string, unknown>, key: string): Record<string, unknown>[] {
  const value = raw[key];
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
}

function numberAt(raw: Record<string, unknown>, key: string): number {
  return Number(raw[key] ?? 0);
}

function stringAt(raw: Record<string, unknown>, key: string): string {
  const value = raw[key];
  return typeof value === 'string' ? value : '';
}

function nullableStringAt(raw: Record<string, unknown>, key: string): string | null {
  const value = raw[key];
  return typeof value === 'string' ? value : null;
}
